import React from "react";
import { MicLevelBars } from "./MicLevelBars";
import {
    ACTION_LABELS,
    hearingLine,
    micCopy,
    MicAction,
    MicTone,
    pausedLine,
    readyLine,
    SILENCE_LINE,
} from "./micCopy";
import type { MicPlatform } from "./micPlatform";
import { MIC_BLOCKING_STATES, MicStateId } from "./micStatus";

export interface BarMessage {
    type: "error" | "warning" | "info";
    message: string;
}

export interface MicStatusBarProps {
    mode: "idle" | "recording";
    micState: MicStateId; // idle with showMic only; ignored while recording or on the extension
    platform: MicPlatform;
    micLabel: string | null; // trimmed; idle: selected mic, recording: track label
    recordingStream: MediaStream | null; // recording mode level source
    showMic: boolean; // false on extension transport: no label, no Change, no level
    message: BarMessage | null; // existing recordingMessage
    messageAction?: { label: string; onClick: () => void }; // optional button on the message row
    hideWarnings: boolean; // activeTour rule: hide warning/info, keep errors
    busy: boolean; // a check is running: disable actions
    starting?: boolean; // a start is in progress: the bar offers no actions at all (default false)
    paused?: boolean; // recording mode only: shows the paused line with flat bars (no live metering)
    silent?: boolean; // recording mode only (R23): a silent minute replaces the hearing line, flat bars, no action
    onAction: (a: MicAction) => void;
    listOpen: boolean;
    list?: React.ReactNode; // MicList rendered in place of the bar when listOpen
}

const ICON_PROPS = {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
};

const MicIcon = ({ stroke }: { stroke: string }) => (
    <svg {...ICON_PROPS} stroke={stroke} className="flex-none">
        <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3M8 22h8M12 15a3 3 0 0 1-3-3V5a3 3 0 0 1 6 0v7a3 3 0 0 1-3 3z" />
    </svg>
);

const MicOffIcon = ({ stroke, className = "" }: { stroke: string; className?: string }) => (
    <svg {...ICON_PROPS} stroke={stroke} className={`flex-none ${className}`}>
        <path d="M2 2l20 20M9 9v3a3 3 0 0 0 5.1 2.1M15 9.3V5a3 3 0 0 0-5.9-.6M17 16.9A7 7 0 0 1 5 12v-2M19 10v2c0 .8-.1 1.5-.4 2.2M12 19v3M8 22h8" />
    </svg>
);

const WarningIcon = ({ stroke }: { stroke: string }) => (
    <svg {...ICON_PROPS} stroke={stroke} className="flex-none">
        <path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01" />
    </svg>
);

const ACTION_COARSE = "min-h-[32px] [@media(pointer:coarse)]:min-h-[44px]";
const DARK_ACTION = `${ACTION_COARSE} flex-none whitespace-nowrap border-0 bg-transparent p-[6px] text-[13px] font-semibold leading-[18px] text-[#2ACBD1] hover:underline hover:underline-offset-[3px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2ACBD1] disabled:opacity-50`;

interface ActionsProps {
    actions: MicAction[];
    busy: boolean;
    onAction: (a: MicAction) => void;
    variant: "dark" | "light";
}

const Actions = ({ actions, busy, onAction, variant }: ActionsProps) => (
    <>
        {actions.map((a, i) => (
            <button
                key={a}
                type="button"
                disabled={busy}
                onClick={() => onAction(a)}
                className={
                    variant === "dark"
                        ? DARK_ACTION
                        : `${i === 0 ? "primary-button" : "secondary-button"} button-sm flex-none ${ACTION_COARSE}`
                }
            >
                {ACTION_LABELS[a]}
            </button>
        ))}
    </>
);

// One-line status text: a long mic label ellipsizes, the full text stays available as the tooltip.
const TruncatedLine = ({ text }: { text: string }) => (
    <span className="min-w-0 flex-1 truncate" title={text}>
        {text}
    </span>
);

const TINT: Record<Exclude<MicTone, "dark">, { bg: string; role: "alert" | "status" }> = {
    pink: { bg: "bg-[#FFF2F4]", role: "alert" },
    cream: { bg: "bg-[#FFF9F2]", role: "status" },
};

interface TintedProps {
    tone: "pink" | "cream";
    icon: React.ReactNode;
    title: string;
    body?: string;
    steps?: string[];
    note?: string;
    wayOut?: string;
    actions: MicAction[];
    busy: boolean;
    onAction: (a: MicAction) => void;
    messageAction?: { label: string; onClick: () => void };
}

const TintedRow = ({
    tone,
    icon,
    title,
    body,
    steps,
    note,
    wayOut,
    actions,
    busy,
    onAction,
    messageAction,
}: TintedProps) => {
    const { bg, role } = TINT[tone];
    const shell = `rounded-b-lg ${bg} text-[13px] leading-[18px] text-[#111827]`;
    if (!steps && !wayOut && !note) {
        return (
            <div role={role} className={`${shell} flex min-h-[48px] items-center gap-[10px] py-2 pl-4 pr-3`}>
                {icon}
                <span className="min-w-0 flex-1 font-semibold">
                    {title}
                    {body && (
                        <>
                            <br />
                            <span className="font-normal text-[#374151]">{body}</span>
                        </>
                    )}
                </span>
                <Actions actions={actions} busy={busy} onAction={onAction} variant="light" />
                {messageAction && (
                    <button
                        type="button"
                        onClick={messageAction.onClick}
                        className={`primary-button button-sm flex-none ${ACTION_COARSE}`}
                    >
                        {messageAction.label}
                    </button>
                )}
            </div>
        );
    }
    return (
        <div role={role} className={`${shell} flex flex-col gap-2 py-3 pl-4 pr-3`}>
            <div className="flex gap-[10px]">
                {icon}
                <span className="font-semibold">{title}</span>
            </div>
            {body && <p className="m-0 pl-[26px] text-[#374151]">{body}</p>}
            {steps && (
                <ol className="m-0 list-decimal pl-11 text-[#374151]">
                    {steps.map((s) => (
                        <li key={s}>{s}</li>
                    ))}
                </ol>
            )}
            <div className="flex gap-2 pl-[26px]">
                <Actions actions={actions} busy={busy} onAction={onAction} variant="light" />
            </div>
            {wayOut && <p className="m-0 pl-[26px] text-[12px] leading-4 text-[#374151]">{wayOut}</p>}
            {note && <p className="m-0 pl-[26px] text-[12px] leading-4 text-[#374151]">{note}</p>}
        </div>
    );
};

export const MicStatusBar = (p: MicStatusBarProps) => {
    const { mode, micState, platform, micLabel, recordingStream, showMic, message, hideWarnings, busy, onAction } = p;
    const { messageAction } = p;
    const noActions: MicAction[] = [];
    const actionsFor = (actions: MicAction[]) => (p.starting ? noActions : actions);

    if (p.listOpen && p.list) return <>{p.list}</>;

    const blocking = mode === "idle" && showMic && MIC_BLOCKING_STATES.has(micState);
    const visibleMessage = message && !(hideWarnings && message.type !== "error") ? message : null;

    const tinted = (copy: ReturnType<typeof micCopy>) => (
        <TintedRow
            tone={copy.tone === "dark" ? "pink" : copy.tone}
            icon={<MicOffIcon stroke="#CF2150" className="mt-px" />}
            title={copy.title}
            body={copy.body}
            steps={copy.steps}
            note={copy.note}
            wayOut={copy.wayOut}
            actions={actionsFor(copy.actions)}
            busy={busy}
            onAction={onAction}
        />
    );

    if (blocking) return tinted(micCopy(micState, platform));

    if (visibleMessage) {
        if (visibleMessage.type === "error") {
            return (
                <TintedRow
                    tone="pink"
                    icon={<WarningIcon stroke="#CF2150" />}
                    title={visibleMessage.message}
                    actions={[]}
                    busy={busy}
                    onAction={onAction}
                    messageAction={messageAction}
                />
            );
        }
        return (
            <TintedRow
                tone="cream"
                icon={<WarningIcon stroke="#B28111" />}
                title={visibleMessage.message}
                actions={[]}
                busy={busy}
                onAction={onAction}
            />
        );
    }

    const darkShell =
        "flex items-center gap-[10px] min-h-[40px] pl-4 pr-3 rounded-b-lg bg-[#181F2A] text-[13px] leading-[18px] text-white/85 [@media(pointer:coarse)]:min-h-[44px]";

    // Extension transport: no mic state of any kind, only a recordingMessage can show.
    if (!showMic) return null;

    if (mode === "recording" && p.paused) {
        return (
            <div role="status" className={darkShell}>
                <MicLevelBars stream={null} />
                <TruncatedLine text={pausedLine(micLabel ?? "Microphone")} />
            </div>
        );
    }

    if (mode === "recording" && p.silent) {
        return (
            <div role="status" className={darkShell}>
                <MicLevelBars stream={null} />
                <TruncatedLine text={SILENCE_LINE} />
            </div>
        );
    }

    if (mode === "recording") {
        return (
            <div role="status" className={darkShell}>
                <MicLevelBars stream={recordingStream} />
                <TruncatedLine text={hearingLine(micLabel ?? "Microphone")} />
            </div>
        );
    }

    if (micState === "R12") {
        const copy = micCopy("R12", platform);
        return (
            <div
                role="status"
                className="flex min-h-[56px] items-center gap-[10px] rounded-b-lg bg-[#181F2A] py-2 pl-4 pr-3 text-[13px] leading-[18px] text-white"
            >
                <MicIcon stroke="#2ACBD1" />
                <span className="flex-1">
                    <b className="font-semibold">{copy.title}</b>
                    <br />
                    <span className="text-white/75">{copy.body}</span>
                </span>
                <Actions actions={actionsFor(copy.actions)} busy={busy} onAction={onAction} variant="light" />
            </div>
        );
    }

    if (micState === "error") return tinted(micCopy("error", platform));

    const copy = micCopy("R10", platform);
    return (
        <div role="status" className={darkShell}>
            <MicIcon stroke="rgba(255,255,255,0.7)" />
            <TruncatedLine text={readyLine(micLabel ?? "Microphone")} />
            <Actions actions={actionsFor(copy.actions)} busy={busy} onAction={onAction} variant="dark" />
        </div>
    );
};
